import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api, { getAllSites } from '../../../lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../../store/authStore';
import { useDebouncedValue } from './useDebouncedValue';
import {
  ASSET_TYPE_DEFAULTS,
  ASSET_FREQUENCY_MAP,
} from '../constants/assetConfig';

const DEBOUNCE_MS = 500;

function isAdmin(user) {
  return (user?.role || '').toLowerCase() === 'admin';
}

/**
 * Page-level data hook for Assets.jsx. Owns all queries, mutations,
 * Load More pagination state, import/export handlers, and form state.
 * The page component calls this once and receives everything it needs to render.
 */
export function useAssets() {
  // Hooks only at top – no early return before any hook (avoids "Rendered more hooks" crash)
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const canManageAssets = isAdmin(user);
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sortBy') || '';
  const sortOrder = searchParams.get('sortOrder') || 'asc';
  const [showForm, setShowForm] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    siteId: '',
    assetType: '',
    elementType: '',
    status: searchParams.get('status') || '',
  });

  useEffect(() => {
    if (user !== null && !isAdmin(user)) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    const status = searchParams.get('status');
    if (status) setFilters((prev) => ({ ...prev, status }));
  }, [searchParams]);
  const [formData, setFormData] = useState(() => {
    const defaults = ASSET_TYPE_DEFAULTS['exit_light'];
    return {
      siteId: '',
      assetType: 'exit_light',
      elementType: 'electrical',
      building: '',
      room: '',
      manufacturer: '',
      model: '',
      equipmentCode: '',
      dbNumber: '',
      circuitBreakerNumber: '',
      serialNumber: '',
      installationDate: '',
      testFrequencyMonths: ASSET_FREQUENCY_MAP['exit_light'],
      notes: '',
      conditionRating: '',
      element: defaults.element,
      component: defaults.component,
      itemType: defaults.itemType,
      capacity: '',
    };
  });
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [importAction, setImportAction] = useState('import');
  const [importResult, setImportResult] = useState(null);
  const [page, setPage] = useState(1);
  const [allAssets, setAllAssets] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const queryClient = useQueryClient();

  const debouncedSearch = useDebouncedValue(searchQuery.trim(), DEBOUNCE_MS);

  // Reset cumulative list, page, and selection when filters, sort, or debounced search change
  useEffect(() => {
    setPage(1);
    setAllAssets([]);
    setSelectedIds(new Set());
  }, [filters.siteId, filters.assetType, filters.elementType, filters.status, debouncedSearch, sortBy, sortOrder]);

  const ASSETS_PAGE_SIZE = 50;

  // 'removed' and 'inactive' are DB status values → filter server-side.
  // 'overdue', 'due_soon', 'current' are test_status values → filter client-side.
  const isServerStatusFilter = filters.status === 'removed' || filters.status === 'inactive';

  const queryParams = new URLSearchParams();
  if (filters.siteId) queryParams.set('siteId', filters.siteId);
  if (filters.assetType) queryParams.set('assetType', filters.assetType);
  if (filters.elementType) queryParams.set('elementType', filters.elementType);
  if (debouncedSearch) queryParams.set('search', debouncedSearch);
  if (isServerStatusFilter) queryParams.set('status', filters.status);
  if (sortBy) {
    queryParams.set('sortBy', sortBy);
    queryParams.set('sortOrder', sortOrder === 'desc' ? 'desc' : 'asc');
  }
  queryParams.set('page', String(page));
  queryParams.set('limit', String(ASSETS_PAGE_SIZE));

  // Backend returns { assets: [], pagination: { totalCount, totalPages, hasNextPage, currentPage } }
  const { data: assetsResponse, isLoading, isError, error } = useQuery(
    ['assets', filters.siteId, filters.assetType, filters.elementType, debouncedSearch, filters.status, sortBy, sortOrder, page],
    () => api.get(`/assets?${queryParams.toString()}`).then((res) => res.data),
    { enabled: !!user && canManageAssets }
  );

  // Append or replace allAssets when new data arrives (cumulative state for Load More)
  const responsePage = assetsResponse?.pagination?.currentPage;
  useEffect(() => {
    if (!assetsResponse?.assets || responsePage == null) return;
    if (responsePage !== page) return;
    if (page === 1) {
      setAllAssets(assetsResponse.assets);
    } else {
      setAllAssets((prev) => [...prev, ...assetsResponse.assets]);
    }
  }, [assetsResponse, page, responsePage]);

  const pagination = assetsResponse?.pagination ?? { totalCount: 0, totalPages: 1, hasNextPage: false };
  const hasNextPage = Boolean(pagination?.hasNextPage);

  // Client-side filter: test_status for overdue/due_soon/current; server handles removed/inactive
  const assets = allAssets.filter((a) => {
    if (!isServerStatusFilter && filters.status && a.test_status !== filters.status) return false;
    return true;
  });

  const { data: sites, error: sitesError } = useQuery(
    'sites',
    () => getAllSites(),
    { enabled: !!user && canManageAssets }
  );

  const createMutation = useMutation(
    (data) => api.post('/assets', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets']);
        toast.success('Asset created');
        resetForm();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to create asset'),
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/assets/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets']);
        toast.success('Asset updated');
        resetForm();
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to update asset'),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/assets/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assets']);
        toast.success('Asset deleted');
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to delete asset'),
    }
  );

  const bulkDeleteMutation = useMutation(
    (ids) => api.post('/assets/bulk-delete', { ids }),
    {
      onSuccess: (_data, ids) => {
        setSelectedIds(new Set());
        queryClient.invalidateQueries(['assets']);
        toast.success(`${ids.length} asset${ids.length !== 1 ? 's' : ''} deleted`);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Bulk delete failed'),
    }
  );

  const resetForm = () => {
    setShowForm(false);
    setEditingAsset(null);
    const defaults = ASSET_TYPE_DEFAULTS['exit_light'];
    setFormData({
      siteId: '',
      assetType: 'exit_light',
      elementType: 'electrical',
      building: '',
      room: '',
      manufacturer: '',
      model: '',
      equipmentCode: '',
      dbNumber: '',
      circuitBreakerNumber: '',
      serialNumber: '',
      installationDate: '',
      testFrequencyMonths: ASSET_FREQUENCY_MAP['exit_light'],
      notes: '',
      conditionRating: '',
      element: defaults.element,
      component: defaults.component,
      itemType: defaults.itemType,
      capacity: '',
    });
  };

  const openEdit = (asset) => {
    setEditingAsset(asset);
    const assetType = asset.asset_type || 'exit_light';
    const elementType = asset.element_type || 'electrical';
    const defaults = ASSET_TYPE_DEFAULTS[assetType] || {};
    setFormData({
      siteId: String(asset.site_id),
      assetType,
      elementType,
      building: asset.building || '',
      room: asset.room || '',
      manufacturer: asset.manufacturer || '',
      model: asset.model || '',
      equipmentCode: asset.equipment_code || '',
      dbNumber: asset.db_number || '',
      circuitBreakerNumber: asset.circuit_breaker_number || '',
      serialNumber: asset.serial_number || '',
      installationDate: asset.installation_date
        ? asset.installation_date.split('T')[0]
        : '',
      testFrequencyMonths: asset.test_frequency_months || ASSET_FREQUENCY_MAP[assetType] || 12,
      notes: asset.notes || '',
      conditionRating: asset.condition_rating != null ? String(asset.condition_rating) : '',
      element: asset.element || defaults.element || '',
      component: asset.component || defaults.component || '',
      itemType: asset.item_type || defaults.itemType || '',
      capacity: asset.capacity || '',
    });
    setShowForm(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const sharedFields = {
      assetType: formData.assetType,
      element_type: formData.elementType,
      building: formData.building || undefined,
      room: formData.room || undefined,
      manufacturer: formData.manufacturer || undefined,
      model: formData.model || undefined,
      equipmentCode: formData.equipmentCode || undefined,
      dbNumber: formData.dbNumber || undefined,
      circuitBreakerNumber: formData.circuitBreakerNumber || undefined,
      serialNumber: formData.serialNumber || undefined,
      installationDate: formData.installationDate || undefined,
      testFrequencyMonths: formData.testFrequencyMonths,
      notes: formData.notes || undefined,
      conditionRating: formData.conditionRating ? parseInt(formData.conditionRating, 10) : undefined,
      element: formData.element || undefined,
      component: formData.component || undefined,
      itemType: formData.itemType || undefined,
      capacity: formData.capacity || undefined,
    };
    if (editingAsset) {
      updateMutation.mutate({ id: editingAsset.id, data: sharedFields });
    } else {
      createMutation.mutate({
        siteId: parseInt(formData.siteId, 10),
        ...sharedFields,
      });
    }
  };

  const getTestStatusClass = (status) => {
    if (!status) return '';
    if (status === 'overdue') return 'status-overdue';
    if (status === 'due_soon') return 'status-due-soon';
    return 'status-current';
  };

  const runAssetImport = async ({ preview }) => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }
    setImportLoading(true);
    setImportAction(preview ? 'preview' : 'import');
    setImportResult(null);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);
      const endpoint = preview ? '/import/assets?preview=true' : '/import/assets';
      const { data } = await api.post(endpoint, formDataUpload);
      setImportResult(data);
      if (preview) {
        toast.success(`Preview parsed ${data.previewRows?.length || 0} row(s)`);
      } else {
        queryClient.invalidateQueries(['assets']);
        if (data.imported > 0) toast.success(`Imported ${data.imported} assets`);
      }
      if (data.errors?.length > 0) toast.error(`${data.errors.length} row(s) had errors`);
      if (data.warnings?.length > 0) toast(`${data.warnings.length} row(s) had warnings`, { icon: '⚠️' });
    } catch (err) {
      const fallbackError = preview ? 'Preview failed' : 'Import failed';
      toast.error(err.response?.data?.error || fallbackError);
      setImportResult({ imported: 0, errors: [{ message: err.response?.data?.error || fallbackError }] });
    } finally {
      setImportLoading(false);
      setImportAction('import');
    }
  };

  const handleImportSubmit = async (e) => {
    e.preventDefault();
    await runAssetImport({ preview: false });
  };

  const handlePreviewClick = async () => {
    await runAssetImport({ preview: true });
  };

  const closeImportModal = () => {
    setShowImportModal(false);
    setImportFile(null);
    setImportResult(null);
    setImportAction('import');
  };

  const handleExportAssetsCsv = async () => {
    setExportLoading(true);
    const loadingId = toast.loading('Preparing assets CSV...');
    try {
      const exportParams = new URLSearchParams();
      if (filters.siteId) exportParams.set('siteId', filters.siteId);
      if (filters.assetType) exportParams.set('assetType', filters.assetType);
      if (filters.elementType) exportParams.set('elementType', filters.elementType);
      if (filters.status) exportParams.set('status', filters.status);
      if (debouncedSearch) exportParams.set('search', debouncedSearch);
      if (sortBy) {
        exportParams.set('sortBy', sortBy);
        exportParams.set('sortOrder', sortOrder === 'desc' ? 'desc' : 'asc');
      }

      const response = await api.get(`/assets/export?${exportParams.toString()}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      let filename = 'assets_export.csv';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch?.[1]) filename = fileNameMatch[1];
      }
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss(loadingId);
      toast.success('Assets CSV downloaded');
    } catch (err) {
      console.error('Assets CSV export failed', err);
      toast.dismiss(loadingId);
      toast.error(err.response?.data?.error || 'Failed to export assets CSV');
    } finally {
      setExportLoading(false);
    }
  };

  const previewGeneratedCount = importResult?.previewRows?.filter((r) => r.assetCodeStrategy === 'generate_after_insert').length || 0;

  return {
    // auth/access
    user,
    canManageAssets,
    // search / filter / sort state
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    sortBy,
    sortOrder,
    searchParams,
    setSearchParams,
    debouncedSearch,
    // asset query data
    assets,
    allAssets,
    setAllAssets,
    assetsResponse,
    isLoading,
    isError,
    error,
    pagination,
    hasNextPage,
    page,
    setPage,
    // sites
    sites,
    sitesError,
    // selection
    selectedIds,
    setSelectedIds,
    // test modal
    selectedAsset,
    setSelectedAsset,
    // form
    showForm,
    setShowForm,
    editingAsset,
    formData,
    setFormData,
    // import
    showImportModal,
    setShowImportModal,
    importFile,
    setImportFile,
    importResult,
    setImportResult,
    importLoading,
    importAction,
    previewGeneratedCount,
    // export
    exportLoading,
    // mutations (needed for isLoading / disabled states)
    createMutation,
    updateMutation,
    deleteMutation,
    bulkDeleteMutation,
    // handlers
    resetForm,
    openEdit,
    handleSubmit,
    getTestStatusClass,
    handleImportSubmit,
    handlePreviewClick,
    closeImportModal,
    handleExportAssetsCsv,
  };
}
